// ===== main-nav-bar-list.component.ts =====
import { Component, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { NgxTippyProps } from 'ngx-tippy-wrapper';
import { Subject, takeUntil, filter } from 'rxjs';
import { NavigationMenuItem } from 'src/app/models/owner-dashboard';
import { SharedOwnerDashboardService } from 'src/app/shared/layout/owner-dashboard/shared-owner-dashboard.service';
import { UserDashboardService } from 'src/app/shared/services/user-dashboard.service';
import { slideInOut } from '../../../animations/animations';

@Component({
  selector: 'app-main-nav-bar-list',
  templateUrl: './main-nav-bar-list.component.html',
  styleUrls: ['./main-nav-bar-list.component.css'],
  animations: [slideInOut],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainNavBarListComponent implements OnInit, OnDestroy {
  processing: boolean = false;
  @Input() sideMenu: NavigationMenuItem[] = [];
  @Input() isMobile: boolean = false;
  @Input() isCollapsed: boolean = false;

  private destroy$ = new Subject<void>();
  
  public readonly popoverTippyProps: NgxTippyProps = {
    placement: 'right',
    followCursor: false,
    trigger: 'mouseenter',
    interactive: true,
    appendTo: document.body,
    delay: [200, 0],
    hideOnClick: true
  };

  constructor(
    public sharedOwnerDashboardService: SharedOwnerDashboardService,
    public router: Router,
    public userDashboardService: UserDashboardService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeActiveRoutes();
    this.subscribeToRouteChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeActiveRoutes(): void {
    this.updateActiveMenuItems(this.router.url);
  }

  private subscribeToRouteChanges(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateActiveMenuItems(event.url);
        this.cdr.markForCheck();
      });
  }

  private updateActiveMenuItems(url: string): void {
    this.sideMenu.forEach(item => {
      if (item?.subItems?.length) {
        const hasActiveSubItem = item.subItems.some(sub => url.includes(sub.routerLink));
        if (hasActiveSubItem && !item.expandable?.status) {
          this.sharedOwnerDashboardService.expandSidebarItem(this.sideMenu, item);
        }
      }
    });
  }

  public toggleSidebarItem(item: NavigationMenuItem): void {
    if (this.isCollapsed && item?.subItems?.length && !this.isMobile) return;

    if (item?.subItems?.length) {
      if (item?.expandable?.status) {
        this.sharedOwnerDashboardService.collapseSidebarItem(item);
      } else {
        this.sharedOwnerDashboardService.expandSidebarItem(this.sideMenu, item);
      }
    }

    if (item?.routerLink && this.isMobile) {
      this.userDashboardService.closeMobileCollapse();
    }
    this.cdr.markForCheck();
  }

  public onSubItemClick(item: NavigationMenuItem): void {
    if (this.isCollapsed) {
      this.sharedOwnerDashboardService.expandSidebarItem(this.sideMenu, item);
    }
    if (this.isMobile) {
      this.userDashboardService.closeMobileCollapse();
    }
  }

  public trackByTagId(index: number, item: NavigationMenuItem): string {
    return item.tagId || index.toString();
  }

  public trackBySubItem(index: number, item: NavigationMenuItem): string {
    return item.tagId || `sub-${index}`;
  }
}